import {
    MeshCellBorder,
    MeshCellBorderType
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellBorder';
import {MeshCellSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshViewTargetType} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshViewTargetType';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import * as classnames from 'classnames/bind';
import {computed} from 'mobx';
import {inject, observer} from 'mobx-react';
import * as React from 'react';

const styles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/Mesh.less');
const cx = classnames.bind(styles);

interface IAfterEndMeshCellViewProps
{
    ctx?:IWSPMeshContext;
    colIndex:number | null;
    rowIndex:number | null;
    isNested:boolean;
}

@inject('ctx')
@observer
export class MeshAfterEndCellView extends React.Component<IAfterEndMeshCellViewProps, any>
{
    public render():JSX.Element
    {
        return (
            <div
                data-type={MeshViewTargetType.Cell}
                className={cx('tableAfterEndCell')}
                style={this._style}
            >
                {this._renderBorder()}
            </div>
        );
    }

    private _renderBorder():JSX.Element
    {
        const {colIndex, rowIndex} = this.props;
        const prevCellSettings = this._settingsForPrev;
        const isGridlinesVisible = this._isGridlinesVisible;
        const shouldShowBorderTop:boolean = typeof colIndex === 'number';
        const shouldShowBorderLeft:boolean = typeof rowIndex === 'number';
        let cellBorderTop;
        let cellBorderLeft;

        if( shouldShowBorderTop )
            cellBorderTop = MeshCellBorder.fromBorder(prevCellSettings && prevCellSettings.borderBottom, isGridlinesVisible);

        if( shouldShowBorderLeft )
            cellBorderLeft = MeshCellBorder.fromBorder(prevCellSettings && prevCellSettings.borderRight, isGridlinesVisible);

        const svgWidth = this._width + 3;
        const svgHeight = this._height + 3;

        const borderLeftY1Pos = 1;
        const borderLeftY2Pos = 1;

        let borderTopX1Pos = 1;

        if( cellBorderTop && cellBorderLeft && cellBorderTop.type === MeshCellBorderType.Grid )
        {
            borderTopX1Pos = cellBorderLeft.type === MeshCellBorderType.Grid
                ? 1
                : cellBorderLeft.type === MeshCellBorderType.Thin
                    ? 2
                    : 3; // double
        }
        // left black border over all !!!
        /*else if( /!*cellBorderTop.type !== MeshCellBorderType.Grid && *!/cellBorderLeft
            cellBorderLeft.type !== MeshCellBorderType.Grid &&
            cellBorderLeft.color === '#000000' &&
            cellBorderTop.color !== '#000000' )
        {
            borderTopX1Pos += cellBorderLeft.type === MeshCellBorderType.Single ? 1 : 2;
        }*/

        const borderTopX2Pos = cellBorderTop
            ? cellBorderTop.type === MeshCellBorderType.Grid ? 2 : 1
            : 0;

        return (
            <svg
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                className={cx('cellBorders')}
            >
                {cellBorderLeft && cellBorderLeft.type !== MeshCellBorderType.Double &&
                (
                    <line x1={1.5} y1={borderLeftY1Pos}
                          x2={1.5} y2={svgHeight - borderLeftY2Pos}
                          stroke={cellBorderLeft.color}
                    />
                )}

                {cellBorderLeft && cellBorderLeft.type === MeshCellBorderType.Double &&
                (
                    <line x1={0.5} y1="1"
                          x2={0.5} y2={svgHeight - 1}
                          stroke={cellBorderLeft.color}
                    />
                )}

                {/* ---- */}

                {cellBorderTop && cellBorderTop.type !== MeshCellBorderType.Double &&
                (
                    <line y1={1.5} x1={borderTopX1Pos}
                          y2={1.5} x2={svgWidth - borderTopX2Pos}
                          stroke={cellBorderTop.color}
                    />
                )}

                {cellBorderTop && cellBorderTop.type === MeshCellBorderType.Double &&
                (
                    <line y1={0.5} x1={borderTopX1Pos}
                          y2={0.5} x2={svgWidth - 1}
                          stroke={cellBorderTop.color}
                    />
                )}
            </svg>
        );
    }

    private get _isGridlinesVisible():boolean
    {
        const {ctx} = this.props;
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        return meshState.isGridlinesVisible;
    }

    private get _settingsForPrev():MeshCellSettings | null
    {
        const {ctx, colIndex, rowIndex} = this.props;
        const {sheetSettingsCtrl, sheetId} = ctx;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {colWidths, rowHeights} = meshState;

        const meshLocation = new MeshLocation(
            typeof colIndex !== 'number' ? colWidths.length - 1 : colIndex,
            typeof rowIndex !== 'number' ? rowHeights.length - 1: rowIndex
        );

        return sheetSettingsCtrl.getCellSettings(sheetId, meshLocation.asKey());
    }

    @computed
    private get _width():number
    {
        const {ctx, colIndex} = this.props;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {colWidths, details: {resizingColIndex, resizingColWidth}, afterEndPartWidth} = meshState;

        if( typeof colIndex !== 'number' )
            return afterEndPartWidth;

        return resizingColIndex === colIndex && resizingColWidth != null
            ? resizingColWidth
            : colWidths[colIndex];
    }

    @computed
    private get _height():number
    {
        const {ctx, rowIndex} = this.props;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {rowHeights, details: {resizingRowIndex, resizingRowHeight}, afterEndPartHeight} = meshState;

        if( typeof rowIndex !== 'number' )
            return afterEndPartHeight;

        return resizingRowIndex === rowIndex && resizingRowHeight != null
            ? resizingRowHeight
            : rowHeights[rowIndex];
    }

    @computed
    private get _style():React.CSSProperties
    {
        return {
            width: `${this._width}px`
        };
    }
}
